import { Col, Form, Input, InputRef, Row } from 'antd';
import Box from 'components/Box';
import CurrencyInputPrecision from 'components/CurrencyInputPrecision';
import Modal from 'components/Modal';
import Text from 'components/Text';
import Icon from 'assets/Icons';
import { debounce, sumBy } from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { IServiceItemData } from 'services/shop/types/categories';
import styled from 'styled-components';
import { formatCurrency } from 'utils/formatCurrency';
import Button from 'components/Button';
import SelectMultipleServicesModal, { useSelectMultipleServicesModalRef } from 'components/SelectMultipleServicesModal';
import ChildrenIdsInput from './ChildrenIdsInput';
import ModalAddNewServices, { useModalAddNewServicesRef } from '../ServicesSettingV2/ModalAddNewServices';
type IAddServiceButtonProps = {
  name: number;
  restField: { fieldKey?: number };
  index: number;
  nameField: string;
  clearErrorByIndex: (index: string) => void;
  isShowAddNewServices?: boolean;
};
const AddServiceButton: React.FC<IAddServiceButtonProps> = ({
  name,
  restField,
  index,
  nameField,
  clearErrorByIndex,
  isShowAddNewServices = false,
}) => {
  const form = Form.useFormInstance();
  const inputRef = useRef<InputRef>(null);
  const [open, setOpen] = useState(false);
  const selectMultipleServicesModalRef = useSelectMultipleServicesModalRef();
  const modalAddNewServicesRef = useModalAddNewServicesRef();
  const currentService: IServiceItemData = form.getFieldValue([
    nameField,
    index,
  ]);
  const handleShowModal = () => {
    setOpen(true);
  };
  const handleCloseModal = () => {
    const currentListValues = form.getFieldValue(nameField) || [];
    const updatedItem = {
      ...currentListValues[index],
      orderNumber: currentService?.orderNumber,
      serviceName: currentService?.serviceName,
      duration: currentService?.duration,
      priceSell: currentService?.priceSell,
      supplyFee: currentService?.supplyFee,
      description: currentService?.description,
      urlVideo: currentService?.urlVideo,
    };
    currentListValues[index] = updatedItem;
    form.setFieldsValue({ [nameField]: currentListValues });
    setOpen(false);
  };
  const handleSubmitModal = async () => {
    try {
      await form.validateFields([
        [nameField, index, 'orderNumber'],
        [nameField, index, 'serviceName'],
        [nameField, index, 'duration'],
        [nameField, index, 'priceSell'],
        [nameField, index, 'supplyFee'],
        [nameField, index, 'description'],
        [nameField, index, 'urlVideo'],
      ]);
      clearErrorByIndex(String(index));
      setOpen(false);
    } catch (error) {
    }
  };

  const focusInput = debounce(() => {
    inputRef.current?.focus();
  }, 100);

  useEffect(() => {
    if (open && inputRef.current) {
      focusInput();
    }
  }, [open, inputRef]);

  const onPriceChange = (value: number) => {
    if (value > 50) {
      form.setFieldValue([nameField, index, 'duration'], 60);
      return;
    }
    if (value > 20) {
      form.setFieldValue([nameField, index, 'duration'], 30);
      return;
    }
    form.setFieldValue([nameField, index, 'duration'], 15);
  };

  const handleAddServices = async () => {
    const currentChildServices = form.getFieldValue([nameField, index, 'childServices']) || [];
    const selectedServices = await selectMultipleServicesModalRef.current?.open({
      selectedCategoryId: '',
      selectedServiceIds: currentChildServices.map((item: IServiceItemData) => item.id) || [],
      baseServices: currentChildServices,
    });
    if (selectedServices) {
      form.setFieldValue([nameField, index, 'childServices'], selectedServices);
      const totalPriceSell = sumBy(selectedServices, (item: IServiceItemData) => item.priceSell);
      form.setFieldValue([nameField, index, 'priceSell'], totalPriceSell);
    }
  };

  const handleAddNewServices = async () => {
    const newService = await modalAddNewServicesRef.current?.open();
    if (newService) {
      const currentChildServices = form.getFieldValue([nameField, index, 'childServices']) || [];
      const updatedChildServices = [...currentChildServices, newService];
      form.setFieldValue([nameField, index, 'childServices'], updatedChildServices);
      const totalPriceSell = sumBy(updatedChildServices, (item: IServiceItemData) => item.priceSell);
      form.setFieldValue([nameField, index, 'priceSell'], totalPriceSell);
    }
  };

  return (
    <AddServiceButtonStyled>
      <ServiceStyled onClick={handleShowModal}>
        <Box className="center" gap="1" flexDirection="column">
          <Text variant="H9" color="text_3">
            {currentService?.serviceName || 'Name'}
          </Text>
          <Box className="center" gap="1" flexWrap="wrap">
            <Text variant="CONTENT_1" color="text_3">
              Duration:{' '}
            </Text>
            <Text variant="H9" color="text_3">
              {currentService?.duration || 0}m
            </Text>
          </Box>

          <Box className="center" gap="1" flexWrap="wrap">
            <Text variant="CONTENT_1" color="text_3">
              Supply Fee:{' '}
            </Text>
            <Text variant="H9" color="text_3">
              {formatCurrency(currentService?.supplyFee || 0)}
            </Text>
          </Box>
        </Box>
        <Box className="center" gap="1" flexWrap="wrap">
          <Text variant="CONTENT_1" color="text_3">
            Sell price:{' '}
          </Text>
          <Text>{formatCurrency(currentService?.priceSell)}</Text>
        </Box>
      </ServiceStyled>
      <Modal
        modalTitle="NEW SERVICE"
        visible={open}
        onClose={handleCloseModal}
        onSubmit={handleSubmitModal}
        okTitle="Save"
        cancelTitle="Close"
        width={'62rem'}
        noneBodyStyle
        noneFooterStyle
        footer={null}
      >
        <FormLayoutStyled>
          <Row gutter={[24, 16]}>
            <Col span={12}>
              <FormFieldWrapper>
                <Text variant="CONTENT_1" color="text_3">
                  Ordinal Number
                </Text>
                <Form.Item
                  {...restField}
                  name={[name, 'orderNumber']}
                  rules={[
                    { required: true, message: 'Missing Ordinal Number' },
                    {
                      validator: async (_, value) => {
                        if (value > 0) return;
                        throw new Error('Number must be greater than 0');
                      },
                    },
                  ]}
                >
                  <Input type="number" />
                </Form.Item>
              </FormFieldWrapper>
            </Col>

            <Col span={12}>
              <FormFieldWrapper>
                <Text variant="CONTENT_1" color="text_3">
                  Service Name
                </Text>
                <Form.Item
                  {...restField}
                  name={[name, 'serviceName']}
                  rules={[{ required: true, message: 'Missing service name' }]}
                >
                  <Input tabIndex={1} placeholder="e.g. Waxing" ref={inputRef} />
                </Form.Item>
              </FormFieldWrapper>
            </Col>

            <Col span={12}>
              <FormFieldWrapper>
                <Text variant="CONTENT_1" color="text_3">
                  Sell Price
                </Text>
                <Form.Item
                  {...restField}
                  name={[name, 'priceSell']}
                  rules={[
                    { required: true, message: 'Missing priceSell' },
                    {
                      validator: async (_, value) => {
                        if (value >= 0) return;
                        throw new Error('Number must be greater than 0');
                      },
                    },
                  ]}
                >
                  <CurrencyInputPrecision tabIndex={2} onChangeDebounce={onPriceChange} />
                </Form.Item>
              </FormFieldWrapper>
            </Col>

            <Col span={12}>
              <FormFieldWrapper>
                <Text variant="CONTENT_1" color="text_3">
                  Duration (Minutes)
                </Text>
                <Form.Item
                  {...restField}
                  name={[name, 'duration']}
                  rules={[
                    { required: true, message: 'Missing duration' },
                    {
                      validator: async (_, value) => {
                        if (value >= 0) return;
                        throw new Error('Number must be greater than 0');
                      },
                    },
                  ]}
                >
                  <Input type="number" tabIndex={4} />
                </Form.Item>
              </FormFieldWrapper>
            </Col>

            <Col span={12}>
              <FormFieldWrapper>
                <Text variant="CONTENT_1" color="text_3">
                  Supply Fee
                </Text>
                <Form.Item
                  {...restField}
                  name={[name, 'supplyFee']}
                >
                  <CurrencyInputPrecision tabIndex={3} />
                </Form.Item>
              </FormFieldWrapper>
            </Col>

            <Col span={12}>
              <FormFieldWrapper>
                <Text variant="CONTENT_1" color="text_3">
                  Input URL
                </Text>
                <Form.Item
                  {...restField}
                  name={[name, 'urlVideo']}
                >
                  <Input
                    tabIndex={6}
                    placeholder="https://..."
                    prefix={<Icon type="link" />}
                  />
                </Form.Item>
              </FormFieldWrapper>
            </Col>

            <Col span={24}>
              <FormFieldWrapper>
                <Text variant="CONTENT_1" color="text_3">
                  Description
                </Text>
                <Form.Item
                  {...restField}
                  name={[name, 'description']}
                >
                  <Input.TextArea
                    rows={4}
                    placeholder="Enter service description..."
                    tabIndex={5}
                  />
                </Form.Item>
              </FormFieldWrapper>
            </Col>
            <Col span={24}>
              <Form.Item name={[name, 'childServices']}>
                <ChildrenIdsInput onAddServices={handleAddServices} onAddNewServices={handleAddNewServices} isShowAddNewServices={isShowAddNewServices} />
              </Form.Item>
            </Col>
          </Row>
          <ButtonGroupStyled>
            <Button ntype="SECONDARY" block onClick={handleCloseModal}>
              Close
            </Button>
            <Button ntype="PRIMARY" block onClick={handleSubmitModal}>
              Save
            </Button>
          </ButtonGroupStyled>
        </FormLayoutStyled>
      </Modal>
      <SelectMultipleServicesModal ref={selectMultipleServicesModalRef} />
      <ModalAddNewServices ref={modalAddNewServicesRef} />
    </AddServiceButtonStyled>
  );
};

export default AddServiceButton;
const AddServiceButtonStyled = styled.div`
  border-radius: 5px;
  background: var(--fill-fill-3, #e5e5e5);
  padding: 4px;
`;
const ServiceStyled = styled.div`
  padding: 0.5rem 0;
  display: flex;
  justify-content: space-between;
  flex-direction: column;
  min-height: 9rem;
  text-align: center;
  border-radius: 5px;
  border: 1px dashed var(--line-line-3, #86909c);
  background: var(--fill-fill-3, #e5e5e5);
`;

const FormLayoutStyled = styled.div`
  padding: 1.5rem;
  .ant-input-affix-wrapper {
    .ant-input-prefix {
      display: flex;
      align-items: center;
      margin-right: 0.5rem;
    }
  }
  
  .ant-input,
  .ant-input-affix-wrapper,
  .ant-input-number,
  textarea.ant-input {
    border-color: rgba(0, 0, 0, 0.15) !important;
    
    &:hover {
      border-color: rgba(0, 0, 0, 0.25) !important;
    }
    
    &:focus,
    &.ant-input-focused,
    &.ant-input-affix-wrapper-focused {
      border-color: rgba(0, 0, 0, 0.3) !important;
      box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.06) !important;
    }
  }
`;

const FormFieldWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const ButtonGroupStyled = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;